import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react'
import {
  BasicInformationProps,
  BasicInfoFormData
} from '../interfaces/formDataInterface'
import axiosReq from '../interceptor'
import useDismissAlert from '../Hooks/useDismissAlert'
import CustomSelect from '../components/CustomSelect/customSelect'
import { toast } from 'react-toastify'
import { MessageAlert } from '../components/message-alert'

const BasicInformation: React.FC<BasicInformationProps> = ({
  isLoading,
  setIsLoading,
  areFieldsSet,
  onFieldsSet,
  onNext
}) => {
  const [formData, setFormData] = useState<BasicInfoFormData>({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    gender: '',
    referral_code: ''
  })
  const [error, setError] = useDismissAlert({ message: '' }, 5000)

  // Fix "Cannot update a component while rendering a different component" warning by moving onFieldsSet to useEffect.
  useEffect(() => {
    const allFieldsSet =
      formData.first_name.trim() !== '' &&
      formData.last_name.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.date_of_birth.trim() !== '' &&
      formData.gender.trim() !== ''
    onFieldsSet(allFieldsSet)
  }, [formData, onFieldsSet])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }))
  }

  const handleGenderSelect = (selectedValue: string) => {
    setFormData((prevData) => ({
      ...prevData,
      gender: selectedValue
    }))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      setIsLoading(true)
      const payload = { ...formData }
      if (payload.referral_code === '') {
        delete payload.referral_code
      }

      const dob = new Date(`${formData.date_of_birth}`)
      const age = new Date().getFullYear() - dob.getFullYear()
      const isMinor =
        age < 18 || (age === 18 && new Date().getTime() < dob.getTime())

      if (isMinor) {
        toast.warning('You must be at least 16 years old!')
        return
      }

      const { status } = await axiosReq.post(
        '/app/v2/auth/sign-up-basic-info',
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      if (status === 200) {
        localStorage.setItem('email', formData.email)

        // Trigger the next step.
        onNext()
      }
    } catch (error: any) {
      console.error('Error submitting form:', error)
      setError({
        message: error.response.data.message
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleFormReset = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      date_of_birth: '',
      gender: '',
      referral_code: ''
    })
    onFieldsSet(false) // Reset the fields set status.
  }

  return (
    <section>
      <form onSubmit={handleSubmit} onReset={handleFormReset}>
        <div className="form-outline mb-4">
          <label className="form-label text-white" htmlFor="first_name">
            First Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            className="form-control"
            placeholder="Enter your first name"
            required
            value={formData.first_name}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>
        <div className="form-outline mb-4">
          <label className="form-label text-white" htmlFor="last_name">
            Last Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            className="form-control"
            placeholder="Enter your last name"
            required
            value={formData.last_name}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>
        <div className="form-outline mb-4">
          <label className="form-label text-white" htmlFor="email">
            Email <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className="form-control"
            placeholder="Enter your Email"
            required
            value={formData.email}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>

        <div className="form-outline mb-4">
          <label className="form-label text-white" htmlFor="date_of_birth">
            Date of Birth <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            className="form-control"
            placeholder="Enter your Date of Birth"
            required
            value={formData.date_of_birth}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </div>

        <div className="form-outline mb-4">
          <label htmlFor="gender" className="form-label text-white">
            Gender <span className="text-danger">*</span>
          </label>
          <CustomSelect
            id="gender"
            placeholder="Select Gender"
            options={['Male', 'Female']}
            selected={formData.gender}
            onSelect={handleGenderSelect}
            isSearchable={false}
          />
        </div>

        <div className="form-outline mb-4">
          <label className="form-label text-white" htmlFor="referral_code">
            Referral Code (Optional)
          </label>
          <input
            type="text"
            id="referral_code"
            name="referral_code"
            className="form-control"
            placeholder="Enter your referral code"
            value={formData.referral_code}
            onChange={handleInputChange}
            disabled={isLoading}
            maxLength={8}
          />
        </div>

        {error.message && <MessageAlert message={error.message} type="error" />}

        <button
          className="btn btn-primary rounded-pill w-100 btn-block"
          type="submit"
          disabled={!areFieldsSet || isLoading}
        >
          Create Account
        </button>
      </form>
    </section>
  )
}

export default BasicInformation
